import {ApiClient} from '@hconnect/apiclient'
import {useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import type {CostAvoidanceAssetValuesForRange, CostAvoidanceForRange} from '../api'
import {getCostAvoidanceForRangePath} from '../api/schedule.endpoints'

export const COST_AVOIDANCE_FOR_RANGE_QUERY_KEY = 'costAvoidanceForRange'

export type CostAvoidanceForRangeData = CostAvoidanceForRange & {
  total: CostAvoidanceAssetValuesForRange
}

interface UseCostAvoidanceForRangeQueryParams {
  plantId: string
  scheduleId: number
  from: string
  to: string
  enabled?: boolean
}

export const useCostAvoidanceForRangeQuery = ({
  plantId,
  scheduleId,
  from,
  to,
  enabled = true
}: UseCostAvoidanceForRangeQueryParams) =>
  useQuery<CostAvoidanceForRange, AxiosError, CostAvoidanceForRangeData | undefined>({
    queryKey: [COST_AVOIDANCE_FOR_RANGE_QUERY_KEY, {plantId, scheduleId, from, to}],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<CostAvoidanceForRange>(
        getCostAvoidanceForRangePath(plantId, scheduleId),
        {params: {from, to}, signal}
      )
      return response.data
    },
    enabled,
    select: (data) => {
      if (!data.assets) {
        return undefined
      }
      const total = Object.values(data.assets).reduce<CostAvoidanceAssetValuesForRange>(
        (prev, next) => ({
          electricityConsumed: prev.electricityConsumed + next.electricityConsumed,
          electricityCost: prev.electricityCost + next.electricityCost,
          costAvoidance: prev.costAvoidance + next.costAvoidance,
          producedTonnes: prev.producedTonnes + next.producedTonnes
        }),
        {
          electricityConsumed: 0,
          electricityCost: 0,
          costAvoidance: 0,
          producedTonnes: 0
        }
      )

      return {...data, total}
    }
  })
